import React, { useCallback, useEffect, useState } from 'react';
import { forkJoin } from 'rxjs';
import { PageError, PageErrorType } from 'pages/page-error';
import { Loading } from 'components/block/loading';
import { Outlet } from 'react-router-dom';
import { getListMasterData } from 'services/master-data';
import { useSubscription } from 'hooks/use-subscription.hook';
import ChatContext from '../context';
import Sidebar from './sidebar';
import Header from './header';
import { IConversation } from 'types/chat';
import { useSession } from 'hooks/use-session.hook';
import { ModalContainer } from 'services/modal';
import { getConversations as getConversationsList } from 'api/chat/get-conversations';
import { MqttService } from 'services/mqtt';
import { NewSubMessage } from 'services/mqtt/mqtt.service';
import { TopicType } from 'consts/common.const';
import { getUserObject } from 'api/chat/user/get-user-object';
import { ContactType } from '../consts';
import { cloneDeep } from 'lodash';

enum Initializing {
  OK,
  RUNNING,
  FAIL,
}

type Contact = {
  [key: string]: any;
};

export interface IUserObject {
  _id: string;
  emp_name: string;
  path_avatar: string;
  user_id: number;
}

export default function ChatLayout() {
  const [initializing, setInitializing] = useState<Initializing>(Initializing.RUNNING);
  const [conversations, setConversations] = useState<Contact[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [pinnedConversations, setPinnedConversations] = useState<Contact[]>([]);
  const [userObject, setUserObject] = useState<IUserObject>(null);
  const [newMsg, setNewMsg] = useState<NewSubMessage<IConversation>>(null);
  const [contactType, setContactType] = useState<ContactType>(ContactType.CONVERSATION);
  const subscription = useSubscription();
  const { userInfo } = useSession();

  const updateConversation = useCallback(
    (convers: IConversation[]) => {
      let pinnedConversationList = [];
      let conversationList = [];

      convers.forEach((conversation: IConversation) => {
        if (!!conversation.members_pin_chat?.length && conversation.members_pin_chat.includes(userObject._id)) {
          pinnedConversationList.push(conversation);
        } else {
          conversationList.push(conversation);
        }
      });

      setConversations(conversationList);
      setPinnedConversations(pinnedConversationList);
    },
    [userObject?._id]
  );

  const getConversations = useCallback(() => {
    if (!userInfo?.userId) return;

    const sub = getConversationsList().subscribe({
      next: (res) => {
        const convers = res.data as IConversation[];

        updateConversation(convers);
      },
    });

    subscription.add(sub);
  }, [subscription, updateConversation, userInfo?.userId]);

  useEffect(() => {
    if (newMsg && newMsg.type === TopicType.ON_UPDATE_CONVERSATION_BY_ID && newMsg.data) {
      const conversation = newMsg.data;

      if (!!conversation.members_pin_chat?.length && conversation.members_pin_chat.includes(userObject._id)) {
        const index = pinnedConversations.findIndex((item) => item._id === conversation._id);

        if (index < 0) {
          setPinnedConversations([conversation, ...pinnedConversations]);

          return;
        }

        pinnedConversations.splice(index, 1, conversation);
        setPinnedConversations([...pinnedConversations]);
      } else {
        const index = conversations.findIndex((item) => item._id === conversation._id);

        if (index < 0) {
          setPinnedConversations([conversation, ...conversations]);

          return;
        }

        conversations.splice(index, 1, conversation);
        setConversations([...conversations]);
      }

      setNewMsg(null);
    }
  }, [conversations, newMsg, pinnedConversations, userObject?._id]);

  useEffect(() => {
    getConversations();
  }, [subscription, userInfo?.userId, getConversations]);

  const updateNameOfConversation = useCallback(
    (_id: string, chat_name: string) => {
      const cloneConversations: Contact[] = cloneDeep(conversations);

      cloneConversations.forEach((conversation: Contact) => {
        if (conversation._id === _id) {
          conversation.chat_name = chat_name;
        }

        setConversations(cloneConversations);
      });
    },
    [conversations]
  );

  useEffect(() => {
    MqttService.getData((res) => {
      setNewMsg(res);
    });
  }, []);

  useEffect(() => {
    if (!userInfo?.userId) return;

    forkJoin({
      masterDataRes: getListMasterData(['masterData']),
      getUserObjectRes: getUserObject(userInfo.userId),
    }).subscribe({
      next: ({ getUserObjectRes }) => {
        setUserObject(getUserObjectRes.data);
        setInitializing(Initializing.OK);
      },
      error: () => setInitializing(Initializing.FAIL),
    });
  }, [subscription, userInfo?.userId]);

  if (initializing === Initializing.RUNNING) {
    return <Loading />;
  }

  if (initializing === Initializing.FAIL) {
    return <PageError error={PageErrorType.APP_LOAD_FAILED} />;
  }

  return (
    <ChatContext.Provider
      value={{
        userObject,
        conversations,
        pinnedConversations,
        getConversations,
        setContactType,
        updateNameOfConversation,
        selectedConversation,
        setSelectedConversation,
      }}
    >
      <div className='chat-wrapper'>
        <Sidebar contactType={contactType} />

        <div className='chat-wrapper-content'>
          <Header />

          <Outlet />

          <ModalContainer />
        </div>
      </div>
    </ChatContext.Provider>
  );
}
